import { BaseAPI } from '../../shared/BaseAPI';
import { LoginResponse, SignupRequest, CheckLeaseResponse } from '@/types/residents';
import { AuthEndpoints } from '../api/endpoints/AuthEndpoints';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { User } from '@/types/residents/user';
import { updateValidationConfig } from '@/utils/residents/validation';
import { API_V1, API_V2 } from '@/constants/residents/api';

// Config response types matching iOS implementation
interface ConfigData {
  minimumPasswordLength?: { value: number };
  maximumPasswordLength?: { value: number };
  // Add other config properties as needed
}

interface ConfigResponse {
  data: ConfigData;
  success: boolean;
  message?: string;
}3

class AuthAPIService extends BaseAPI {
  private static authInstance: AuthAPIService;

  static getInstance(): AuthAPIService {
    if (!AuthAPIService.authInstance) {
      AuthAPIService.authInstance = new AuthAPIService();
    }
    return AuthAPIService.authInstance;
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    // Clear any existing tokens before login attempt
    await SecureStorage.getInstance().clearTokens();
    
    const response = await this.post<LoginResponse>(AuthEndpoints.login().url, {
      email,
      password,
    });
    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.post(AuthEndpoints.logout().url);
    } catch (error) {
      // Logout should succeed even if API fails
    }
  }

  async checkLease(): Promise<CheckLeaseResponse> {
    const response = await this.get<CheckLeaseResponse>(AuthEndpoints.checkLease().url);
    return response;
  }

  async signup(data: SignupRequest): Promise<{ success: boolean; message: string }> {
    // Support both resident (type: 1) and guarantor/parent (type: 2) matching resio-web
    const payload = {
      email: data.email,
      type: data.type || 1, // Default to resident if not specified
    };
    
    const response = await this.post<{ success: boolean; message: string }>(
      AuthEndpoints.signup().url,
      payload
    );
    return response;
  }

  async createAccount(email: string): Promise<{ success: boolean; message: string }> {
    // Direct method matching iOS createAccount API call
    const payload = {
      email: email,
      type: 1, // Resident type
    };
    
    const response = await this.post<{ success: boolean; message: string }>(
      AuthEndpoints.signup().url,
      payload
    );
    return response;
  }

  async forgotPassword(email: string, targetPath?: string): Promise<{ success: boolean; message: string }> {
    // Include targetPath parameter matching web apps
    const payload: { email: string; targetPath?: string } = { email };
    if (targetPath) {
      payload.targetPath = targetPath;
    }
    
    const response = await this.post<{ success: boolean; message: string }>(
      AuthEndpoints.verifyEmail().url,
      payload
    );
    return response;
  }

  async resetPassword(token: string, password: string): Promise<LoginResponse> {
    const response = await this.post<LoginResponse>(AuthEndpoints.resetPassword().url, {
      token,
      password,
    });
    return response;
  }

  async sendVerification(email: string, targetPath?: string): Promise<{ success: boolean; message: string }> {
    // Matching web apps sendVerificationEmail API call with targetPath parameter
    const payload: { email: string; targetPath?: string } = { email };
    if (targetPath) {
      payload.targetPath = targetPath;
    }
    
    const response = await this.post<{ success: boolean; message: string }>(
      AuthEndpoints.verifyEmail().url,
      payload
    );
    return response;
  }

  async verifyEmail(email: string, code: string): Promise<{ success: boolean }> {
    const response = await this.post<{ success: boolean }>(AuthEndpoints.verifyEmail().url, {
      email,
      code,
    });
    return response;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.get<{ response: User }>(`${API_V1}/me`);
    return response.response;
  }

  async updateUser(data: Partial<User>): Promise<User> {
    const response = await this.put<{ response: User }>(`${API_V2}/me`, data);
    return response.response;
  }

  // Matches iOS setBio: PUT /api/v1/users/{userID} with { roommateBio }
  async updateBio(userId: number, roommateBio: string): Promise<User> {
    const response = await this.put<{ response: User }>(`${API_V1}/${userId}`, {
      roommateBio,
    });
    return response.response;
  }

  // Matches iOS setProfileImage: PUT /api/v1/users/{userID} with { photo: base64 }
  async updateProfileImage(userId: number, base64Image: string): Promise<User> {
    const response = await this.put<{ response: User }>(`${API_V1}/${userId}`, {
      photo: base64Image,
    });
    return response.response;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
    const response = await this.post<{ success: boolean }>(AuthEndpoints.changePassword().url, {
      currentPassword,
      newPassword,
    });
    return response;
  }

  async deleteAccount(password: string): Promise<{ success: boolean }> {
    const response = await this.delete<{ success: boolean }>(`${API_V2}/me`, {
      data: { password },
    });
    return response;
  }

  async fetchConfig(): Promise<ConfigResponse> {
    try {
      const response = await this.get<ConfigResponse>(AuthEndpoints.config().url);
      
      // Update validation config if successful, matching iOS behavior
      if (response.success && response.data) {
        const validationConfig = {
          minimumPasswordLength: response.data.minimumPasswordLength?.value,
          maximumPasswordLength: response.data.maximumPasswordLength?.value,
        };
        updateValidationConfig(validationConfig);
      }
      
      return response;
    } catch (error) {
      console.warn('Failed to fetch config:', error);
      // Return default config on error
      return {
        success: false,
        data: {},
        message: 'Failed to fetch configuration',
      };
    }
  }
}

export const AuthAPI = AuthAPIService.getInstance(); 